<template>
  <div class="field-editor">
    <!-- Empty State -->
    <div v-if="!field" class="empty-state">
      <div class="empty-icon">
        <i class="bx bx-pointer"></i>
      </div>
      <h5>Propriedades do Campo</h5>
      <p>Selecione um campo para editar suas propriedades</p>
    </div>

    <!-- Field Properties -->
    <template v-else>
      <div class="editor-header">
        <h5>
          Propriedades do Campo
          <span v-if="isStandard" class="badge badge-soft-primary ml-2" style="font-size: 10px; vertical-align: middle;">
            Padrao
          </span>
          <span v-if="isCompanionField" class="badge badge-info ml-2" style="font-size: 10px; vertical-align: middle;">
            Acompanhante
          </span>
          <span v-if="isRegistrationControlField" class="badge ml-2" style="font-size: 10px; vertical-align: middle; background: #fde8d0; color: #d35400;">
            Ctrl. Inscrição
          </span>
        </h5>
        <span class="field-type-label">{{ field.fieldType ? field.fieldType.label : 'Campo' }}</span>
      </div>

      <div class="editor-content">
        <!-- Basic Settings Section -->
        <div class="settings-section">
          <div class="section-title">Configurações Básicas</div>

          <div class="form-group">
            <label>
              Rótulo do Campo
              <i v-if="isCompanionField" class="bx bx-lock-alt text-danger ml-1" title="Campo de acompanhante fixo"></i>
            </label>
            <input
              v-model="localField.label"
              type="text"
              class="form-control"
              placeholder="Digite o rótulo"
              :readonly="isCompanionField"
              :disabled="isCompanionField"
              @input="emitUpdate"
            />
            <small v-if="isCompanionField" class="text-muted">
              Campo de acompanhante fixo (não editável)
            </small>
          </div>

          <!-- Campo: Nome do Campo (ID) - Comentado para não confundir o usuário -->
          <!--
          <div class="form-group">
            <label>
              Nome do Campo (ID)
              <i v-if="isStandard || isSystemField" class="bx bx-lock-alt text-danger ml-1" title="Identificador do sistema fixo"></i>
            </label>
            <input
              v-model="localField.name"
              type="text"
              class="form-control"
              placeholder="nome_do_campo"
              :readonly="isStandard || isSystemField"
              :disabled="isStandard || isSystemField"
              @input="emitUpdate"
            />
            <small class="text-muted">
              {{ (isStandard || isSystemField) ? 'Identificador do sistema fixo' : 'Use snake_case, sem espaços' }}
            </small>
          </div>
          -->

          <!-- RC field info -->
          <div v-if="isRegistrationControlField" class="rc-field-info mb-3">
            <div class="rc-info-box">
              <i class="bx bx-info-circle"></i>
              <span>Campo preenchido automaticamente após validação do identificador. Somente leitura.</span>
            </div>
          </div>

          <div class="form-group" v-if="!isRegistrationControlField && !isTermsAcceptanceField && !isFakeTicketField && !isDisclaimerField && !isInstrucoesField">
            <label>Placeholder</label>
            <input
              v-model="localField.placeholder"
              type="text"
              class="form-control"
              placeholder="Texto de exemplo..."
              @input="emitUpdate"
            />
          </div>

          <div class="form-group" v-if="!isFakeTicketField && !isDisclaimerField && !isInstrucoesField">
            <label>Texto de Ajuda (Opcional)</label>
            <input
              v-model="localField.help_text"
              type="text"
              class="form-control"
              placeholder="Adicione uma dica para o usuário..."
              @input="emitUpdate"
            />
          </div>

          <div class="form-group" v-if="!isFakeTicketField && !isDisclaimerField && !isInstrucoesField">
            <label>Link no Rótulo (Opcional)</label>
            <div class="d-flex" style="gap: 8px;">
              <input
                v-model="localField.label_link_text"
                type="text"
                class="form-control"
                placeholder="Texto do link (ex: Ver tabela)"
                maxlength="120"
                @input="emitUpdate"
              />
              <input
                v-model="localField.label_link_url"
                type="url"
                class="form-control"
                placeholder="https://..."
                maxlength="500"
                @input="emitUpdate"
              />
            </div>
            <small class="text-muted">
              Aparece como link clicável ao lado do rótulo, abre em nova aba. Os dois campos precisam estar preenchidos.
            </small>
          </div>

          <div class="form-group toggle-group" v-if="!isRegistrationControlField && !isFakeTicketField && !isDisclaimerField && !isInstrucoesField">
            <label>
              Campo Obrigatório
              <i v-if="isFieldRequiredLocked" class="bx bx-lock-alt text-danger ml-1" title="Campo obrigatório fixo"></i>
            </label>
            <b-form-checkbox
              v-model="localField.required"
              :disabled="isFieldRequiredLocked"
              switch
              @change="emitUpdate"
            />
          </div>

          <!-- Campo de Uso Interno - Removido (implementado no formbuilder de configuração de fichas) -->
          <!--
          <div class="form-group toggle-group">
            <label>
              Campo de Uso Interno
              <small class="text-muted d-block" style="font-weight: normal; margin-top: 4px;">
                Visível apenas no painel administrativo
              </small>
            </label>
            <b-form-checkbox
              v-model="localField.is_internal"
              switch
              @change="emitUpdate"
            />
            <small v-if="localField.is_internal" class="text-info d-block mt-1">
              💡 Este campo não aparecerá no formulário público
            </small>
          </div>
          -->

          <!-- Visibility Toggle for optional standard fields (CPF, phone, gender) -->
          <div v-if="isOptionalStandardField" class="form-group toggle-group">
            <label>
              Campo Ativo
              <small class="text-muted d-block" style="font-weight: normal; margin-top: 4px;">
                Quando inativo, o campo não aparece no formulário
              </small>
            </label>
            <b-form-checkbox
              v-model="localField.is_visible"
              switch
              @change="emitUpdate"
            />
          </div>
        </div>

        <!-- Options Section (for select, radio, checkbox) - hidden for RC fields -->
        <div v-if="hasOptions && !isRegistrationControlField" class="settings-section">
          <div class="section-title">Opções</div>

          <div class="options-list">
            <div
              v-for="(opt, idx) in localOptions"
              :key="idx"
              class="option-item"
            >
              <input
                v-model="localOptions[idx].label"
                type="text"
                class="form-control"
                placeholder="Nome da opção"
                @input="updateOptions"
              />
              <button
                type="button"
                class="btn-remove-option"
                @click="removeOption(idx)"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>

          <button type="button" class="btn-add-option" @click="addOption">
            <i class="bx bx-plus"></i> Adicionar Opção
          </button>
        </div>

        <!-- Terms Acceptance Config Section -->
        <div v-if="isTermsAcceptanceField" class="settings-section">
          <div class="section-title">Conteúdo dos Termos</div>

          <div class="form-group">
            <label>Texto dos Termos (HTML)</label>
            <textarea
              v-model="localField.config.terms_content"
              class="form-control"
              rows="10"
              placeholder="<h3>1. Título</h3><p>Texto do termo...</p>"
              @input="emitUpdate"
            ></textarea>
            <small class="text-muted">Suporta HTML: &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.</small>
          </div>

          <div class="form-group">
            <label>Texto do Aceite</label>
            <input
              v-model="localField.config.checkbox_label"
              type="text"
              class="form-control"
              placeholder="Declaro que li e concordo..."
              @input="emitUpdate"
            />
            <small class="text-muted">Texto exibido ao lado do checkbox de aceite</small>
          </div>

          <div class="form-group toggle-group">
            <label>
              Exigir Leitura Completa
              <small class="text-muted d-block" style="font-weight: normal; margin-top: 4px;">
                Checkbox só habilita após rolar até o final
              </small>
            </label>
            <b-form-checkbox
              v-model="localField.config.require_scroll"
              switch
              @change="emitUpdate"
            />
          </div>
        </div>

        <!-- Fake Ticket Config Section -->
        <div v-if="isFakeTicketField" class="settings-section">
          <div class="section-title">Configuração do Bilhete</div>

          <div class="form-group">
            <label>Título do Ingresso</label>
            <input
              v-model="localField.config.ticket_title"
              type="text"
              class="form-control"
              placeholder="Ex: Acesso Completo — ACELERA 2025"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Valor Original (R$)</label>
            <input
              v-model="localField.config.ticket_value"
              type="text"
              class="form-control"
              placeholder="Ex: 450,00"
              @input="emitUpdate"
            />
            <small class="text-muted">Valor que aparecerá riscado no bilhete</small>
          </div>

          <div class="form-group">
            <label>Validade</label>
            <input
              v-model="localField.config.ticket_validity"
              type="text"
              class="form-control"
              placeholder="Ex: 15 Mar 2025"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Tipo do Ingresso</label>
            <input
              v-model="localField.config.ticket_type"
              type="text"
              class="form-control"
              placeholder="Ex: Presencial, Online, VIP"
              @input="emitUpdate"
            />
          </div>
        </div>

        <!-- Disclaimer Config Section -->
        <div v-if="isDisclaimerField" class="settings-section">
          <div class="section-title">Configuração do Disclaimer</div>

          <div class="form-group">
            <label>Título</label>
            <input
              v-model="localField.config.title"
              type="text"
              class="form-control"
              placeholder="Ex: Como gerar seu código de participação"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Texto Descritivo</label>
            <ckeditor
              :editor="disclaimerEditor"
              v-model="localField.config.body"
              :config="disclaimerEditorConfig"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Cor do Componente</label>
            <select v-model="localField.config.color" class="form-control" @change="emitUpdate">
              <option value="amber">Amber (Aviso)</option>
              <option value="info">Azul (Informativo)</option>
              <option value="danger">Vermelho (Importante)</option>
              <option value="purple">Roxo (Destaque)</option>
              <option value="neutral">Neutro</option>
            </select>
          </div>

          <div class="form-group toggle-group">
            <label>
              Exibir Passo a Passo
              <div class="custom-toggle">
                <input type="checkbox" v-model="localField.config.show_steps" @change="emitUpdate" />
                <span class="toggle-track"></span>
              </div>
            </label>
          </div>

          <div v-if="localField.config.show_steps" class="form-group">
            <label>Passos</label>
            <div v-for="(step, idx) in localField.config.steps" :key="idx" class="disclaimer-step-editor">
              <span class="step-num">{{ idx + 1 }}</span>
              <div class="step-editor-wrapper">
                <ckeditor
                  :editor="disclaimerEditor"
                  v-model="step.text"
                  :config="disclaimerEditorConfig"
                  @input="emitUpdate"
                />
              </div>
              <button class="btn btn-sm btn-link text-danger p-0" @click="removeDisclaimerStep(idx)" title="Remover passo">
                <i class="bx bx-trash"></i>
              </button>
            </div>
            <button class="btn btn-sm btn-outline-primary mt-1" @click="addDisclaimerStep">
              <i class="bx bx-plus mr-1"></i> Adicionar Passo
            </button>
          </div>
        </div>

        <!-- Instruções Config Section -->
        <div v-if="isInstrucoesField" class="settings-section">
          <div class="section-title">Conteúdo das Instruções</div>

          <div class="form-group">
            <label>Texto</label>
            <ckeditor
              :editor="instrucoesEditor"
              v-model="localField.config.content"
              :config="instrucoesEditorConfig"
              @input="emitUpdate"
            />
            <small class="text-muted d-block mt-1">
              Use a barra de ferramentas para formatar: tamanho de fonte, negrito, cores, alinhamento, listas, etc.
            </small>
          </div>

          <div class="form-group">
            <label>Cor do Componente</label>
            <select v-model="localField.config.color" class="form-control" @change="emitUpdate">
              <option value="green">Verde</option>
              <option value="amber">Amber (Aviso)</option>
              <option value="info">Azul (Informativo)</option>
              <option value="danger">Vermelho (Importante)</option>
              <option value="purple">Roxo (Destaque)</option>
              <option value="neutral">Neutro</option>
            </select>
            <small class="text-muted d-block mt-1">
              Define a cor do banner, do aceite e dos destaques. Só aparece quando banner ou aceite estão ativos.
            </small>
          </div>

          <div class="form-group toggle-group">
            <label>
              Banner de Orientação no Topo
              <div class="custom-toggle">
                <input type="checkbox" v-model="localField.config.show_read_hint" @change="emitUpdate" />
                <span class="toggle-track"></span>
              </div>
            </label>
          </div>

          <div v-if="localField.config.show_read_hint" class="form-group">
            <label>Texto do Banner</label>
            <input
              v-model="localField.config.read_hint_text"
              type="text"
              class="form-control"
              placeholder="Ex: Leia todas as instruções até o final para prosseguir."
              @input="emitUpdate"
            />
          </div>

          <div class="form-group toggle-group">
            <label>
              Exigir Aceite para Prosseguir
              <div class="custom-toggle">
                <input type="checkbox" v-model="localField.config.require_acceptance" @change="emitUpdate" />
                <span class="toggle-track"></span>
              </div>
            </label>
          </div>

          <div v-if="localField.config.require_acceptance" class="form-group">
            <label>Texto do Aceite</label>
            <input
              v-model="localField.config.acceptance_label"
              type="text"
              class="form-control"
              placeholder="Ex: Li e estou ciente de todas as instruções acima."
              @input="emitUpdate"
            />
            <small class="text-muted d-block mt-1">
              O participante precisará marcar essa caixa para avançar no formulário.
            </small>
          </div>
        </div>

        <!-- Terms Link Config Section -->
        <div v-if="isTermsField" class="settings-section">
          <div class="section-title">Link nos Termos</div>

          <div class="form-group">
            <label>Texto do Link</label>
            <input
              v-model="localField.config.link_text"
              type="text"
              class="form-control"
              placeholder="Ex: Termos de Uso e Política de Privacidade"
              @input="emitUpdate"
            />
            <small class="text-muted">Trecho do rótulo que será exibido como link clicável</small>
          </div>

          <div class="form-group">
            <label>URL dos Termos</label>
            <input
              v-model="localField.config.link_url"
              type="text"
              class="form-control"
              placeholder="https://seusite.com/termos"
              @input="emitUpdate"
            />
            <small class="text-muted">URL que abrirá em nova aba ao clicar no link</small>
          </div>
        </div>

        <!-- Conditional Logic Section - hidden for RC fields -->
        <div v-if="!isRegistrationControlField" class="settings-section">
          <div class="section-header">
            <div class="section-title">
              Lógica Condicional
              <i v-if="isCompanionConditionalField" class="bx bx-lock-alt text-danger ml-1" title="Condição fixa de acompanhante"></i>
            </div>
            <b-form-checkbox
              v-model="showConditions"
              :disabled="isCompanionConditionalField"
              switch
              @change="toggleConditions"
            />
          </div>

          <div v-if="showConditions" class="conditions-content">
            <p v-if="isCompanionConditionalField" class="text-info mb-2">
              <i class="bx bx-info-circle"></i> Condição fixa: Exibe somente quando "Levarei acompanhante?" = Sim
            </p>
            <p v-else class="conditions-help">Exibir este campo se:</p>

            <div
              v-for="(condition, idx) in localConditions"
              :key="idx"
              class="condition-row"
            >
              <select
                v-model="condition.field_id"
                class="form-control"
                :disabled="isCompanionConditionalField"
                @change="updateConditions"
              >
                <option value="">Selecione um campo...</option>
                <option
                  v-for="f in availableFields"
                  :key="f.id || f._tempId"
                  :value="f.id || f._tempId"
                >
                  {{ f.label }}
                </option>
              </select>

              <select
                v-model="condition.operator"
                class="form-control"
                :disabled="isCompanionConditionalField"
                @change="onOperatorChange(condition)"
              >
                <option value="==">for igual a</option>
                <option value="!=">for diferente de</option>
                <option value="filled_diff">estiver preenchido e for diferente de</option>
                <option value="contains">contiver</option>
                <option value="not_contains">não contiver</option>
                <option value="in">for uma das opções</option>
                <option value="not_in">não for nenhuma das opções</option>
                <option value="is_empty">estiver vazio</option>
                <option value="is_not_empty">não estiver vazio</option>
              </select>

              <!-- Modo de comparação (só p/ igual/diferente): valor fixo ou outro campo -->
              <select
                v-if="['==', '!=', 'filled_diff'].includes(condition.operator)"
                v-model="condition._vmode"
                class="form-control"
                style="max-width: 140px"
                :disabled="isCompanionConditionalField"
                @change="onVModeChange(condition)"
              >
                <option value="static">um valor fixo</option>
                <option value="field">outro campo</option>
              </select>

              <!-- Lado direito: comparar com OUTRO CAMPO -->
              <select
                v-if="['==', '!=', 'filled_diff'].includes(condition.operator) && condition._vmode === 'field'"
                v-model="condition.value_field_id"
                class="form-control"
                :disabled="isCompanionConditionalField"
                @change="updateConditions"
              >
                <option value="">Selecione o campo...</option>
                <option
                  v-for="f in comparableFields(condition)"
                  :key="f.id || f._tempId"
                  :value="f.id || f._tempId"
                >
                  {{ f.label }}
                </option>
              </select>

              <!-- Lado direito: VALOR FIXO -->
              <input
                v-else-if="!['is_empty', 'is_not_empty'].includes(condition.operator)"
                v-model="condition.value"
                type="text"
                class="form-control"
                :disabled="isCompanionConditionalField"
                :placeholder="['in', 'not_in'].includes(condition.operator) ? 'opcao1, opcao2, opcao3' : 'Valor'"
                @input="updateConditions"
              />

              <button
                v-if="!isCompanionConditionalField"
                type="button"
                class="btn-remove-condition"
                @click="removeCondition(idx)"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>

            <button
              v-if="!isCompanionConditionalField"
              type="button"
              class="btn-add-condition"
              @click="addCondition"
            >
              <i class="bx bx-plus"></i> Adicionar Condição
            </button>

            <div v-if="localConditions.length > 1" class="condition-logic">
              <span>Corresponder</span>
              <select v-model="conditionLogic" class="form-control form-control-sm" @change="updateConditions">
                <option value="and">Todas as condições</option>
                <option value="or">Qualquer condição</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Audience por tag do RC item — aparece se houver item RC com tag OU se o
             campo já tiver uma restrição (pra que ela NUNCA fique impossível de remover) -->
        <div v-if="!isRegistrationControlField && (rcItemTags.length > 0 || hasAudienceRestriction)" class="settings-section">
          <div class="section-title">Exibir apenas para participantes com as tags</div>
          <p class="conditions-help">
            O campo só aparece para quem se inscreveu por um item do Controle de Inscrição
            que tenha as tags marcadas abaixo. Sem nenhuma marcada, o campo aparece para todos.
          </p>
          <div v-if="rcItemTags.length > 0" class="audience-tags">
            <b-form-checkbox
              v-for="tag in rcItemTags"
              :key="tag.id"
              :checked="isAudienceTagSelected(tag.id)"
              @change="toggleAudienceTag(tag.id)"
            >
              {{ tag.name }}
            </b-form-checkbox>
          </div>
          <!-- Restrição órfã: o campo está gateado por tag(s) que não estão mais em uso -->
          <div v-if="hasOrphanAudience" class="alert alert-warning mt-2 mb-0" style="font-size:12px;">
            ⚠️ Este campo está restrito a tag(s) que não estão mais em uso (#{{ orphanAudienceTagIds.join(', #') }}),
            então ele fica <strong>oculto para todos</strong>. Clique abaixo para liberar.
          </div>
          <button
            v-if="hasAudienceRestriction"
            type="button"
            class="btn btn-sm btn-outline-danger mt-2"
            @click="clearAudienceRestriction"
          >
            Remover restrição por tag
          </button>
        </div>

        <!-- Validation Rules Section (for date/birthdate/file fields) -->
        <div v-if="hasValidationOptions" class="settings-section">
          <div class="section-header">
            <div class="section-title">Regras de Validação</div>
            <b-form-checkbox
              v-model="showValidations"
              switch
              @change="toggleValidations"
            />
          </div>

          <div v-if="showValidations" class="validations-content">
            <p class="conditions-help">Validações aplicadas a este campo:</p>

            <div
              v-for="(validation, idx) in localValidations"
              :key="idx"
              class="condition-row"
            >
              <select
                v-model="validation.rule"
                class="form-control"
                @change="onValidationRuleChange(idx)"
              >
                <option value="">Selecione uma regra...</option>
                <option
                  v-for="opt in availableValidationRules"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>

              <!-- Valor numérico (min_age, max_age) -->
              <div v-if="validationNeedsNumber(validation.rule)" class="form-group mb-0">
                <label style="font-size: 11px;">{{ validationValueLabel(validation.rule) }}</label>
                <input
                  v-model.number="validation.years"
                  type="number"
                  class="form-control"
                  min="1"
                  max="150"
                  placeholder="18"
                  @input="updateValidations"
                />
              </div>

              <!-- Quantidade de caracteres (min_length, max_length) -->
              <div v-if="validationNeedsChars(validation.rule)" class="form-group mb-0">
                <label style="font-size: 11px;">{{ validationValueLabel(validation.rule) }}</label>
                <input
                  v-model.number="validation.chars"
                  type="number"
                  class="form-control"
                  min="1"
                  max="10000"
                  :placeholder="validation.rule === 'min_length' ? '1' : '100'"
                  @input="updateValidations"
                />
              </div>

              <!-- Valor de data (date_before, date_after) -->
              <div v-if="validationNeedsDate(validation.rule)" class="form-group mb-0">
                <label style="font-size: 11px;">{{ validationValueLabel(validation.rule) }}</label>
                <select
                  v-model="validation.date"
                  class="form-control"
                  @change="updateValidations"
                >
                  <option value="today">Hoje (data atual)</option>
                  <option value="custom">Data específica...</option>
                </select>
                <input
                  v-if="validation.date === 'custom'"
                  v-model="validation.custom_date"
                  type="date"
                  class="form-control mt-1"
                  @input="updateValidations"
                />
              </div>

              <!-- Tamanho de arquivo (file_size) -->
              <div v-if="validation.rule === 'file_size'" class="form-group mb-0">
                <label style="font-size: 11px;">Tamanho máximo (MB)</label>
                <input
                  v-model.number="validation.max_mb"
                  type="number"
                  class="form-control"
                  min="1"
                  max="100"
                  placeholder="20"
                  @input="updateValidations"
                />
              </div>

              <button
                type="button"
                class="btn-remove-condition"
                @click="removeValidation(idx)"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>

            <button
              type="button"
              class="btn-add-condition"
              @click="addValidation"
            >
              <i class="bx bx-plus"></i> Adicionar Regra
            </button>
          </div>
        </div>

        <!-- Advanced Settings Section -->
        <div class="settings-section">
          <div class="section-title collapsible" @click="showAdvanced = !showAdvanced">
            <span>Configurações Avançadas</span>
            <i :class="showAdvanced ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
          </div>

          <div v-if="showAdvanced" class="advanced-content">
            <!-- Step Assignment -->
            <div v-if="steps.length > 0" class="form-group">
              <label>Etapa</label>
              <select
                v-model="localField.step_id"
                class="form-control"
                @change="emitUpdate"
              >
                <option :value="null">Sem etapa definida</option>
                <option
                  v-for="step in steps"
                  :key="step.id || step._tempId"
                  :value="step.id || step._tempId"
                >
                  {{ step.name }}
                </option>
              </select>
              <small class="text-muted">Em qual etapa este campo será exibido</small>
            </div>

            <!-- Destinatário do campo — só quando o evento usa acompanhante -->
            <div v-if="parseInt(registrationType) === 2" class="form-group">
              <label>Destinatário do campo</label>
              <select v-model="answerTarget" class="form-control">
                <option value="main">Participante principal</option>
                <option v-for="n in 5" :key="'comp-' + n" :value="'companion:' + n">Acompanhante {{ n }}</option>
              </select>
              <small class="text-muted">
                Define a quem pertence a resposta. "Acompanhante" grava a resposta na ficha do acompanhante.
              </small>
            </div>

            <!-- Tipo de anexo — só para campos de upload (file/image) -->
            <div v-if="isFileType" class="form-group">
              <label>Tipo de anexo</label>
              <select
                v-model="localField.attachment_type_id"
                class="form-control"
                @change="emitUpdate"
              >
                <option :value="null">— Detectar automaticamente —</option>
                <option
                  v-for="t in attachmentTypes"
                  :key="t.id"
                  :value="t.id"
                >{{ t.name }}</option>
              </select>
              <small class="text-muted">
                Define em qual categoria o anexo aparece na aba "Anexos" da ficha. Se vazio, o sistema tenta detectar pelo label do campo.
              </small>
            </div>

            <div v-if="!isRegistrationControlField" class="form-group">
              <label>Valor Padrão</label>
              <input
                v-model="localField.default_value"
                type="text"
                class="form-control"
                placeholder="Valor padrão"
                @input="emitUpdate"
              />
            </div>

            <div class="form-group">
              <label>Largura</label>
              <select
                v-model="localLayout.cols"
                class="form-control"
                @change="updateLayout"
              >
                <option value="12">Largura total (100%)</option>
                <option value="6">Meia largura (50%)</option>
                <option value="4">Um terço (33%)</option>
                <option value="3">Um quarto (25%)</option>
              </select>
            </div>

            <template v-if="!isRegistrationControlField">
              <div class="form-group toggle-group">
                <label>Valor Único</label>
                <b-form-checkbox
                  v-model="localField.unique"
                  switch
                  @change="emitUpdate"
                />
              </div>

              <div class="form-group toggle-group">
                <label>Desabilitado</label>
                <b-form-checkbox
                  v-model="localField.disabled"
                  switch
                  @change="emitUpdate"
                />
              </div>

              <div class="form-group toggle-group">
                <label>Somente Leitura</label>
                <b-form-checkbox
                  v-model="localField.readonly"
                  switch
                  @change="emitUpdate"
                />
              </div>

              <div class="form-group toggle-group">
                <label>
                  Enviar para análise quando preenchido
                  <span class="d-block text-muted" style="font-size: 11px; font-weight: 400;">
                    Se este campo vier preenchido na inscrição, o participante entra em
                    "Análise" (ocupando vaga) aguardando aprovação manual da equipe.
                  </span>
                </label>
                <b-form-checkbox
                  v-model="localField.config.triggers_review"
                  switch
                  @change="emitUpdate"
                />
              </div>

              <div v-if="localField.config.triggers_review" class="form-group">
                <label>Status de Análise</label>
                <select
                  class="form-control"
                  v-model="localField.config.review_ref_status_id"
                  @change="emitUpdate"
                >
                  <option :value="null">Selecione…</option>
                  <option v-for="(name, id) in refstatusList" :key="id" :value="parseInt(id)">{{ name }}</option>
                </select>
                <small class="text-muted">
                  Crie o status antes em Configurações → Status de pendências.
                </small>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue'
import FullEditor from 'ckeditor5-build-full'
import { mapState, mapActions } from 'vuex'
import { getRegistrationControlItemTags } from '../services'

export default {
  name: 'FieldEditor',
  components: {
    ckeditor: CKEditor.component
  },
  props: {
    field: {
      type: Object,
      default: null
    },
    allFields: {
      type: Array,
      default: () => []
    },
    fieldTypes: {
      type: Array,
      default: () => []
    },
    steps: {
      type: Array,
      default: () => []
    },
    registrationType: {
      type: [Number, String],
      default: null
    },
    attachmentTypes: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      localField: {},
      localOptions: [],
      localConditions: [],
      localValidations: [],
      localLayout: { cols: '12' },
      conditionLogic: 'and',
      showConditions: false,
      showValidations: false,
      showAdvanced: false,
      // Tags usadas em itens do Controle de Inscrição (para o gate de público).
      // Carregado em created(); se vazio, a seção "Exibir só para tags" não aparece.
      rcItemTags: [],
      disclaimerEditor: FullEditor,
      disclaimerEditorConfig: {
        toolbar: {
          items: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo']
        }
      },
      instrucoesEditor: FullEditor,
      instrucoesEditorConfig: {
        toolbar: {
          items: [
            'heading', '|',
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'fontSize', 'fontColor', 'fontBackgroundColor', '|',
            'alignment', '|',
            'bulletedList', 'numberedList', '|',
            'outdent', 'indent', '|',
            'link', 'blockQuote', 'horizontalLine', '|',
            'undo', 'redo'
          ],
          shouldNotGroupWhenFull: true
        },
        heading: {
          options: [
            { model: 'paragraph', title: 'Parágrafo', class: 'ck-heading_paragraph' },
            { model: 'heading2', view: 'h2', title: 'Título', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Subtítulo', class: 'ck-heading_heading3' }
          ]
        },
        fontSize: {
          options: ['tiny', 'small', 'default', 'big', 'huge']
        }
      }
    }
  },
  created () {
    // Carrega os sub-status (ref_status) para o select de "Status de Análise"
    if (!this.refstatusList || Object.keys(this.refstatusList).length === 0) {
      this.GetRefStatus()
    }
    this.loadRcItemTags()
  },
  computed: {
    ...mapState('settings', ['refstatusList']),
    // Destinatário da resposta do campo: 'main' ou 'companion:N'
    answerTarget: {
      get () {
        if ((this.localField.answer_owner || 'main') === 'companion') {
          return 'companion:' + (this.localField.companion_index || 1)
        }
        return 'main'
      },
      set (val) {
        if (val && val.indexOf('companion:') === 0) {
          this.$set(this.localField, 'answer_owner', 'companion')
          this.$set(this.localField, 'companion_index', parseInt(val.split(':')[1], 10) || 1)
        } else {
          this.$set(this.localField, 'answer_owner', 'main')
          this.$set(this.localField, 'companion_index', null)
        }
        this.emitUpdate()
      }
    },
    hasOptions () {
      // Check fieldType.has_options first
      if (this.localField.fieldType?.has_options) {
        return true
      }
      // Check direct has_options property
      if (this.localField.has_options) {
        return true
      }
      // Check by field format/name
      const optionTypes = ['select', 'multiselect', 'radio', 'checkbox', 'dropdown']
      const format = this.localField.format || this.localField.fieldType?.name || ''
      if (optionTypes.includes(format.toLowerCase())) {
        return true
      }
      // Check if field already has options array
      if (Array.isArray(this.localField.options) && this.localField.options.length > 0) {
        return true
      }
      return false
    },
    availableFields () {
      // Return all fields except the current one
      return this.allFields.filter(f => {
        const currentId = this.field?.id || this.field?._tempId
        return (f.id || f._tempId) !== currentId
      })
    },
    isStandard () {
      // Check if field is a standard field
      return this.localField._isStandard === true ||
             (this.localField.field_type && this.localField.field_type.is_standard === true) ||
             (this.localField.fieldType && this.localField.fieldType.is_standard === true)
    },
    isSystemField () {
      // Check if field is a system field with fixed name (origem, retorno)
      return (this.localField.field_type && this.localField.field_type.is_system_field === true) ||
             (this.localField.fieldType && this.localField.fieldType.is_system_field === true)
    },
    isFieldRequiredLocked () {
      // Lock required checkbox for name and email (disabled = true)
      if (this.isStandard && this.localField.disabled === true) {
        return true
      }

      // Lock required checkbox for companion fields (always required)
      const fieldName = this.localField.name || ''
      const companionRequiredFields = ['has_companion', 'companion_name', 'companion_email']
      const isCompanionField = companionRequiredFields.some(suffix => fieldName.includes(suffix))

      return isCompanionField
    },
    isOptionalStandardField () {
      // Show visibility toggle for CPF, phone, gender (standard but not disabled)
      return this.isStandard && this.localField.disabled === false
    },
    // Restrição "exibir por tag" ativa neste campo
    hasAudienceRestriction () {
      return Array.isArray(this.localField.audience_tag_ids) && this.localField.audience_tag_ids.length > 0
    },
    // Tags da restrição que NÃO estão mais entre as tags de itens (órfãs: tag
    // excluída ou removida dos itens). Com elas, o campo fica oculto pra todos.
    orphanAudienceTagIds () {
      if (!this.hasAudienceRestriction) return []
      const known = new Set((this.rcItemTags || []).map(t => Number(t.id)))
      return this.localField.audience_tag_ids.map(Number).filter(id => !known.has(id))
    },
    hasOrphanAudience () {
      return this.orphanAudienceTagIds.length > 0
    },
    isCompanionField () {
      // Check if field is a companion field (auto-generated)
      const fieldName = this.localField.name || ''
      return fieldName.startsWith('companion_') || this.localField.is_companion_field === true
    },
    isCompanionConditionalField () {
      // Check if is a companion field with fixed conditions (not the has_companion field itself)
      const fieldName = this.localField.name || ''
      return fieldName.startsWith('companion_') &&
             !fieldName.includes('has_companion') &&
             (fieldName.includes('companion_name') ||
              fieldName.includes('companion_email') ||
              fieldName.includes('companion_phone'))
    },
    isRegistrationControlField () {
      return this.localField.config &&
             this.localField.config.source === 'registration_control'
    },
    isTermsAcceptanceField () {
      return this.localField.fieldType && this.localField.fieldType.name === 'terms_acceptance'
    },
    isTermsField () {
      return (this.localField.fieldType && this.localField.fieldType.name === 'terms') ||
             (this.localField.field_type && this.localField.field_type.name === 'terms')
    },
    isFakeTicketField () {
      return this.localField.fieldType && this.localField.fieldType.name === 'fake_ticket'
    },
    isInstrucoesField () {
      return this.localField.fieldType && this.localField.fieldType.name === 'instrucoes'
    },
    isDisclaimerField () {
      return this.localField.fieldType && this.localField.fieldType.name === 'disclaimer'
    },
    isDateType () {
      const ftName = this.localField.fieldType?.name || this.localField.field_type?.name || ''
      return ['date', 'birthdate', 'birth_date'].includes(ftName)
    },
    isBirthdateType () {
      const ftName = this.localField.fieldType?.name || this.localField.field_type?.name || ''
      return ['birthdate', 'birth_date'].includes(ftName)
    },
    isFileType () {
      const ftName = this.localField.fieldType?.name || this.localField.field_type?.name || ''
      return ['file', 'image'].includes(ftName)
    },
    isTextLikeType () {
      // Tipos onde faz sentido limitar tamanho de caracteres digitados.
      // Excluímos os com máscara fixa (cpf, cnpj, cep, phone) — tamanho já
      // é determinado pela máscara.
      const ftName = this.localField.fieldType?.name || this.localField.field_type?.name || ''
      return ['text', 'textarea', 'email', 'string', 'name', 'url', 'password'].includes(ftName)
    },
    hasValidationOptions () {
      return this.isDateType || this.isFileType || this.isTextLikeType
    },
    availableValidationRules () {
      const rules = []
      if (this.isTextLikeType) {
        rules.push({ value: 'min_length', label: 'Mínimo de caracteres' })
        rules.push({ value: 'max_length', label: 'Máximo de caracteres' })
      }
      if (this.isDateType) {
        rules.push({ value: 'date_before', label: 'Data anterior a' })
        rules.push({ value: 'date_after', label: 'Data posterior a' })
      }
      if (this.isBirthdateType) {
        rules.push({ value: 'min_age', label: 'Idade mínima' })
        rules.push({ value: 'max_age', label: 'Idade máxima' })
      }
      if (this.isFileType) {
        rules.push({ value: 'file_size', label: 'Tamanho máximo do arquivo' })
        rules.push({ value: 'file_type', label: 'Tipos de arquivo permitidos' })
      }
      return rules
    }
  },
  watch: {
    field: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val) {
          this.localField = { ...val }
          // Ensure config object exists for terms_acceptance, terms, and fake_ticket fields.
          // Importante: campos sem config vêm como array vazio [] do backend (array_merge
          // de arrays vazios). Um array é "truthy", então precisamos convertê-lo p/ objeto —
          // senão chaves setadas nele (ex.: triggers_review) somem no JSON.stringify do save.
          if (!this.localField.config || Array.isArray(this.localField.config)) {
            this.$set(this.localField, 'config', {})
          }
          // Gate de público por tag — garante array reativo (Vue 2)
          if (!Array.isArray(this.localField.audience_tag_ids)) {
            this.$set(this.localField, 'audience_tag_ids', [])
          }
          // Roteamento para Análise: garante as chaves (reatividade do toggle e select)
          if (this.localField.config.triggers_review === undefined) {
            this.$set(this.localField.config, 'triggers_review', false)
          }
          if (this.localField.config.review_ref_status_id === undefined) {
            this.$set(this.localField.config, 'review_ref_status_id', null)
          }
          // Ensure fake_ticket config defaults exist
          const ftNameCheck = this.localField.fieldType?.name || this.localField.field_type?.name
          if (ftNameCheck === 'fake_ticket') {
            if (this.localField.config.ticket_title === undefined) {
              this.$set(this.localField.config, 'ticket_title', 'Acesso Completo — Evento 2025')
            }
            if (this.localField.config.ticket_value === undefined) {
              this.$set(this.localField.config, 'ticket_value', '450,00')
            }
            if (this.localField.config.ticket_validity === undefined) {
              this.$set(this.localField.config, 'ticket_validity', '15 Mar 2025')
            }
            if (this.localField.config.ticket_type === undefined) {
              this.$set(this.localField.config, 'ticket_type', 'Presencial')
            }
          }
          // Ensure disclaimer config defaults exist
          if (ftNameCheck === 'disclaimer') {
            if (this.localField.config.title === undefined) {
              this.$set(this.localField.config, 'title', 'Informação importante')
            }
            if (this.localField.config.body === undefined) {
              this.$set(this.localField.config, 'body', '')
            }
            if (this.localField.config.color === undefined) {
              this.$set(this.localField.config, 'color', 'amber')
            }
            if (this.localField.config.show_steps === undefined) {
              this.$set(this.localField.config, 'show_steps', false)
            }
            if (this.localField.config.steps === undefined) {
              this.$set(this.localField.config, 'steps', [])
            }
          }
          // Ensure instrucoes config defaults exist
          if (ftNameCheck === 'instrucoes') {
            if (this.localField.config.content === undefined) {
              this.$set(this.localField.config, 'content', '<p>Insira aqui o texto de instruções para o participante.</p>')
            }
            if (this.localField.config.color === undefined) {
              this.$set(this.localField.config, 'color', 'green')
            }
            if (this.localField.config.require_acceptance === undefined) {
              this.$set(this.localField.config, 'require_acceptance', false)
            }
            if (this.localField.config.acceptance_label === undefined) {
              this.$set(this.localField.config, 'acceptance_label', 'Li e estou ciente de todas as instruções acima.')
            }
            if (this.localField.config.show_read_hint === undefined) {
              this.$set(this.localField.config, 'show_read_hint', false)
            }
            if (this.localField.config.read_hint_text === undefined) {
              this.$set(this.localField.config, 'read_hint_text', 'Leia todas as instruções até o final para prosseguir.')
            }
          }
          // Ensure link_text/link_url exist for terms fields (use $set for Vue 2 reactivity)
          const ftName = this.localField.fieldType?.name || this.localField.field_type?.name
          if (ftName === 'terms') {
            if (this.localField.config.link_text === undefined) {
              this.$set(this.localField.config, 'link_text', '')
            }
            if (this.localField.config.link_url === undefined) {
              this.$set(this.localField.config, 'link_url', '')
            }
          }
          this.localOptions = Array.isArray(val.options) ? [...val.options] : []
          this.localLayout = val.layout ? { ...val.layout } : { cols: '12' }

          // Handle validations
          // Detecta troca de campo para resetar estado local
          const fieldChanged = (val.id || val._tempId) !== (this._lastFieldId)
          this._lastFieldId = val.id || val._tempId

          if (fieldChanged || !this.showValidations) {
            const rawValidations = val.validations || []
            if (Array.isArray(rawValidations) && rawValidations.length > 0) {
              this.showValidations = true
              this.localValidations = rawValidations
                .filter(v => v && v.rule)
                .map(v => {
                  const item = { rule: v.rule }
                  if (v.years !== undefined) item.years = v.years
                  if (v.chars !== undefined) item.chars = v.chars
                  if (v.date !== undefined) item.date = v.date
                  if (v.custom_date !== undefined) item.custom_date = v.custom_date
                  if (v.max_kb !== undefined) item.max_mb = Math.round(v.max_kb / 1024)
                  if (v.types !== undefined) item.types = v.types
                  return item
                })
            } else if (fieldChanged) {
              this.showValidations = false
              this.localValidations = []
            }
          }

          // Handle conditions — só reconstrói o estado local ao TROCAR de campo
          // (senão o eco do emitUpdate reseta o _vmode "outro campo" no meio da edição).
          if (fieldChanged || !this.showConditions) {
            if (val.conditions && val.conditions.length > 0) {
              this.showConditions = true
              // Flatten conditions if nested in rules
              if (val.conditions[0] && val.conditions[0].rules) {
                this.localConditions = [...val.conditions[0].rules]
                this.conditionLogic = val.conditions[0].logic || 'and'
              } else {
                this.localConditions = [...val.conditions]
              }
              // Normalizar field_id: se não bate com id/_tempId de nenhum campo,
              // tentar encontrar por name (resolve referências órfãs de companion fields)
              this.localConditions.forEach(cond => {
                // Modo da UI: "outro campo" se a regra já compara com value_field_id
                this.$set(cond, '_vmode', cond.value_field_id ? 'field' : 'static')
                if (!cond.field_id) return
                const fieldIdStr = String(cond.field_id)
                const matchById = this.allFields.find(f =>
                  String(f.id) === fieldIdStr || String(f._tempId) === fieldIdStr
                )
                if (!matchById) {
                  // Tenta encontrar por nome parcial (ex: companion_1_has_companion)
                  const matchByName = this.allFields.find(f =>
                    f.name && fieldIdStr.includes(f.name)
                  )
                  if (matchByName) {
                    cond.field_id = matchByName.id || matchByName._tempId
                  }
                }
              })
            } else {
              this.showConditions = false
              this.localConditions = []
            }
          }
        } else {
          this.localField = {}
          this.localOptions = []
          this.localConditions = []
          this.localValidations = []
          this.localLayout = { cols: '12' }
          this.showConditions = false
          this.showValidations = false
        }
      }
    }
  },
  methods: {
    ...mapActions('settings', { GetRefStatus: 'GetRefStatusList' }),
    emitUpdate () {
      this.$emit('update-field', { ...this.localField })
    },
    // Gate de público por tag do RC item
    async loadRcItemTags () {
      try {
        const res = await getRegistrationControlItemTags()
        this.rcItemTags = res.data || []
      } catch (e) {
        this.rcItemTags = []
      }
    },
    isAudienceTagSelected (tagId) {
      return Array.isArray(this.localField.audience_tag_ids) &&
        this.localField.audience_tag_ids.map(Number).includes(Number(tagId))
    },
    toggleAudienceTag (tagId) {
      if (!Array.isArray(this.localField.audience_tag_ids)) {
        this.$set(this.localField, 'audience_tag_ids', [])
      }
      const ids = this.localField.audience_tag_ids.map(Number)
      const idx = ids.indexOf(Number(tagId))
      if (idx === -1) {
        ids.push(Number(tagId))
      } else {
        ids.splice(idx, 1)
      }
      this.localField.audience_tag_ids = ids
      this.emitUpdate()
    },
    // Remove toda a restrição por tag (escape hatch p/ restrições órfãs que não
    // dá pra desmarcar pelos checkboxes — ex.: tag excluída/removida dos itens).
    clearAudienceRestriction () {
      this.localField.audience_tag_ids = []
      this.emitUpdate()
    },
    // Disclaimer steps
    addDisclaimerStep () {
      if (!this.localField.config.steps) {
        this.$set(this.localField.config, 'steps', [])
      }
      this.localField.config.steps.push({ text: '' })
      this.emitUpdate()
    },
    removeDisclaimerStep (idx) {
      this.localField.config.steps.splice(idx, 1)
      this.emitUpdate()
    },
    // Options
    addOption () {
      this.localOptions.push({ label: '', value: '' })
      this.updateOptions()
    },
    removeOption (idx) {
      this.localOptions.splice(idx, 1)
      this.updateOptions()
    },
    updateOptions () {
      // Value sempre igual ao label para manter consistência com condições
      this.localOptions = this.localOptions.map(opt => ({
        label: opt.label,
        value: opt.label
      }))
      this.localField.options = [...this.localOptions]
      this.emitUpdate()
    },
    // Layout
    updateLayout () {
      this.localField.layout = { ...this.localLayout }
      this.emitUpdate()
    },
    // Conditions
    toggleConditions () {
      if (!this.showConditions) {
        this.localConditions = []
        this.localField.conditions = []
        this.emitUpdate()
      }
    },
    addCondition () {
      this.localConditions.push({
        field_id: '',
        operator: '==',
        value: '',
        value_field_id: '',
        _vmode: 'static'
      })
      this.updateConditions()
    },
    removeCondition (idx) {
      this.localConditions.splice(idx, 1)
      this.updateConditions()
    },
    // Tipos de campo elegíveis como "outro campo" na comparação (v1: select/radio/texto),
    // excluindo o próprio campo gatilho da regra.
    comparableFields (condition) {
      const allowed = ['select', 'radio', 'text']
      return this.availableFields.filter(f => {
        // exclui o próprio campo gatilho da regra
        if (String(f.id || f._tempId) === String(condition.field_id)) return false
        const ftName = (f.fieldType && f.fieldType.name) || (f.field_type && f.field_type.name) || f.type || ''
        // tipo permitido OU desconhecido (campo ainda não salvo, sem o objeto fieldType)
        return ftName === '' || allowed.includes(ftName)
      })
    },
    onVModeChange (condition) {
      // Alterna entre comparar com valor fixo x outro campo; limpa o lado oposto.
      if (condition._vmode === 'field') {
        this.$set(condition, 'value', '')
      } else {
        this.$set(condition, 'value_field_id', '')
      }
      this.updateConditions()
    },
    onOperatorChange (condition) {
      // "outro campo" só vale p/ igual/diferente/preenchido-e-diferente; nos demais, valor fixo.
      if (!['==', '!=', 'filled_diff'].includes(condition.operator)) {
        this.$set(condition, '_vmode', 'static')
        this.$set(condition, 'value_field_id', '')
      }
      this.updateConditions()
    },
    updateConditions () {
      if (this.localConditions.length > 0) {
        // Monta rules limpas: persiste value OU value_field_id (nunca o _vmode da UI).
        const rules = this.localConditions.map(c => {
          const rule = { field_id: c.field_id, operator: c.operator }
          if (['==', '!=', 'filled_diff'].includes(c.operator) && c._vmode === 'field' && c.value_field_id) {
            rule.value_field_id = c.value_field_id
          } else {
            rule.value = c.value
          }
          return rule
        })
        this.localField.conditions = [{
          logic: this.conditionLogic,
          rules
        }]
      } else {
        this.localField.conditions = []
      }
      this.emitUpdate()
    },
    // Validations
    toggleValidations () {
      if (!this.showValidations) {
        this.localValidations = []
        this.localField.validations = []
        this.emitUpdate()
      }
    },
    addValidation () {
      this.localValidations.push({ rule: '' })
      this.updateValidations()
    },
    removeValidation (idx) {
      this.localValidations.splice(idx, 1)
      this.updateValidations()
    },
    onValidationRuleChange (idx) {
      const v = this.localValidations[idx]
      // Set defaults based on rule type
      if (v.rule === 'min_age' && v.years === undefined) {
        this.$set(v, 'years', 18)
      }
      if (v.rule === 'max_age' && v.years === undefined) {
        this.$set(v, 'years', 120)
      }
      if ((v.rule === 'date_before' || v.rule === 'date_after') && v.date === undefined) {
        this.$set(v, 'date', 'today')
      }
      if (v.rule === 'file_size' && v.max_mb === undefined) {
        this.$set(v, 'max_mb', 20)
      }
      this.updateValidations()
    },
    updateValidations () {
      const formatted = this.localValidations
        .filter(v => v.rule)
        .map(v => {
          const item = { rule: v.rule }
          if (v.rule === 'min_age' || v.rule === 'max_age') {
            item.years = v.years || 18
          }
          if (v.rule === 'min_length' || v.rule === 'max_length') {
            item.chars = v.chars || (v.rule === 'min_length' ? 1 : 100)
          }
          if (v.rule === 'date_before' || v.rule === 'date_after') {
            item.date = v.date === 'custom' && v.custom_date ? v.custom_date : (v.date || 'today')
          }
          if (v.rule === 'file_size') {
            item.max_kb = (v.max_mb || 20) * 1024
          }
          if (v.rule === 'file_type' && v.types) {
            item.types = v.types
          }
          return item
        })
      this.localField.validations = formatted
      this.emitUpdate()
    },
    validationNeedsNumber (rule) {
      return ['min_age', 'max_age'].includes(rule)
    },
    validationNeedsChars (rule) {
      return ['min_length', 'max_length'].includes(rule)
    },
    validationNeedsDate (rule) {
      return ['date_before', 'date_after'].includes(rule)
    },
    validationValueLabel (rule) {
      const labels = {
        min_age: 'Anos',
        max_age: 'Anos',
        min_length: 'Caracteres',
        max_length: 'Caracteres',
        date_before: 'Anterior a',
        date_after: 'Posterior a'
      }
      return labels[rule] || 'Valor'
    }
  }
}
</script>

<style scoped>
.field-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 24px;
  color: #9ca3af;
}

.empty-state h5 {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.empty-state p {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

/* Editor Header */
.editor-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.editor-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 4px 0;
}

.field-type-label {
  font-size: 12px;
  color: #6b7280;
}

/* Editor Content */
.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.editor-content::-webkit-scrollbar {
  width: 6px;
}

.editor-content::-webkit-scrollbar-track {
  background: transparent;
}

.editor-content::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.editor-content::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Settings Sections */
.settings-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
}

.settings-section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}

.section-title.collapsible {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
  padding: 8px 0;
}

.section-title.collapsible:hover {
  color: #556ee6;
}

/* Form Groups */
.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group .form-control {
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.form-group .form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.form-group small {
  display: block;
  margin-top: 4px;
  font-size: 11px;
}

.toggle-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-group label {
  margin-bottom: 0;
}

/* Options */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  gap: 8px;
}

.option-item .form-control {
  flex: 1;
}

.btn-remove-option {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
}

.btn-remove-option:hover {
  background: #fee2e2;
  color: #dc2626;
}

.btn-add-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  width: 100%;
  justify-content: center;
}

.btn-add-option:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Conditions */
.conditions-content {
  margin-top: 16px;
}

.conditions-help {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

.condition-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 8px;
  position: relative;
}

.condition-row .form-control {
  font-size: 12px;
  padding: 6px 10px;
}

.btn-remove-condition {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
}

.btn-remove-condition:hover {
  background: #fee2e2;
  color: #dc2626;
}

.btn-add-condition {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #556ee6;
  font-size: 13px;
  cursor: pointer;
}

.btn-add-condition:hover {
  text-decoration: underline;
}

.condition-logic {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: #6b7280;
}

.condition-logic .form-control {
  width: auto;
  font-size: 12px;
  padding: 4px 8px;
}

/* Validations (reuses condition styles) */
.validations-content {
  margin-top: 16px;
}

/* Advanced Content */
.advanced-content {
  margin-top: 16px;
}

/* Registration Control Field Info */
.rc-info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #fffaf5;
  border: 1px solid #fde8d0;
  border-radius: 6px;
  font-size: 12px;
  color: #d35400;
  line-height: 1.4;
}

.rc-info-box i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.disclaimer-step-editor {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.disclaimer-step-editor .step-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f0f0f0;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  flex-shrink: 0;
  margin-top: 6px;
}

.disclaimer-step-editor .step-editor-wrapper {
  flex: 1;
  min-width: 0;
}

/* CKEditor compact styling inside field editor */
.settings-section .ck.ck-editor {
  width: 100%;
}

.settings-section .ck.ck-editor__main > .ck-editor__editable {
  min-height: 60px;
  max-height: 180px;
  font-size: 13px;
}

.disclaimer-step-editor .ck.ck-editor__main > .ck-editor__editable {
  min-height: 40px;
  max-height: 120px;
  font-size: 12px;
}
</style>
